import { Fragment, Suspense, lazy, useEffect, useState, useMemo, useRef } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { mapErrors } from "../../../Utils";
import { createNote } from "../../../../api/notes";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import PMCombobox from "../../../utils/PMCombobox";
import { reportStatusesOptions } from "./utils";
import { updateReportAsync } from "../../../../slices/service/reports";
import { useDispatch } from "react-redux";
import SimplePrompt from "../../../utils/simplePrompt";
import PMSwitch from "../../../utils/PMSwitch";
import { PMField } from "../../../utils/PMField";
const QuillEditor = lazy(() =>
  import("../../../utils/quill-editor").then((m) => ({ default: m.QuillEditor }))
);
import { Link, useNavigate } from "react-router-dom";
import { TWclassNames } from "../../../utils/Div";
import { ReportTypeSelector } from "../../../utils/moduleSelectors/reportTypeSelector";
import { EnhancedChecklistSelector } from "./enhanced-checklist-selector";
import { EnhancedItemsSelector } from "./enhanced-items-selector";
import { createReportChecklist } from "../../../../api/service/report";
import { reportHasVariableDataForCopy } from "../../../../utils/reportVariablesUi";

export const ReportRectifyDialog = (props) => {
  const { onLoading, onClose, open, isLoading, report } = props;
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // Memoize initialValues to prevent unnecessary reinitialization
  const initialValues = useMemo(() => ({
    id: report?.id,
    description: "",
    description_text: "",
    internal_description: "",
    internal_description_text: "",
    copy_variables: false,
    copy_price_lines: false,
    sequel: true,
    report_type: report?.report_type?.id || null,
    checklist_config: null,
    items_config: null,
    submit: null,
  }), [report?.id, report?.report_type?.id]);

  const navigate = useNavigate();

  const [deletePrompt, setDeletePrompt] = useState(false);
  const [checklistSelectorOpen, setChecklistSelectorOpen] = useState(false);
  const [selectedChecklistConfig, setSelectedChecklistConfig] = useState(null);
  const [itemsSelectorOpen, setItemsSelectorOpen] = useState(false);
  const [selectedItemsConfig, setSelectedItemsConfig] = useState(null);
  
  // Use ref to store formik instance to prevent reinitialization
  const formikRef = useRef(null);

  const handleChecklistSelect = (config) => {
    setSelectedChecklistConfig(config);
    setChecklistSelectorOpen(false);
  };

  const handleItemsSelect = (config) => {
    setSelectedItemsConfig(config);
    setItemsSelectorOpen(false);
  };

  const createChecklistForSequel = async (sequelReportId, config) => {
    if (!config || config.mode === "new") {
      // Create empty checklist
      try {
        await createReportChecklist({
          report: {
            id: sequelReportId,
          },
          fields: "id,checklist",
        });
      } catch (err) {
        console.error("Error creating empty checklist:", err);
      }
    } else if (config.mode === "type") {
      // Create checklist from type with selected items
      try {
        const response = await createReportChecklist({
          report: {
            id: sequelReportId,
            checklist_type_id: config.checklist_type_id,
            selected_item_ids: config.selected_item_ids,
          },
          fields: "id,checklist",
        });
        return response;
      } catch (err) {
        console.error("Error creating checklist from type:", err);
        throw err;
      }
    } else if (config.mode === "main_report" && config.items) {
      // Create checklist from main report items
      try {
        const response = await createReportChecklist({
          report: {
            id: sequelReportId,
            copy_from_checklist_items: config.items,
          },
          fields: "id,checklist",
        });
        return response;
      } catch (err) {
        console.error("Error creating checklist from main report:", err);
        throw err;
      }
    }
  };

  // Reset form values when dialog closes or report changes
  useEffect(() => {
    if (!open) {
      // Reset form when dialog closes
      if (formikRef.current) {
        formikRef.current.resetForm({ values: initialValues });
      }
      setSelectedChecklistConfig(null);
      setSelectedItemsConfig(null);
    }
  }, [open, report?.id, initialValues]);

  // Show sequel report link if it exists
  if (report?.sequelReport?.id) {
    return (
      <>
        <Transition.Root show={open} as={Fragment}>
          <Dialog as="div" className="relative z-10" onClose={onClose}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </Transition.Child>

            <div className="fixed inset-0 z-10 overflow-y-auto">
              <div className="flex min-h-full justify-center p-4 text-center sm:items-center sm:p-0">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                  enterTo="opacity-100 translate-y-0 sm:scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                  leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                  <Dialog.Panel className="w-full sm:max-w-2xl max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                    <div className="mt-3 sm:mt-5">
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-medium leading-6 text-gray-900"
                      >
                        {t("Create a follow-up report")}
                      </Dialog.Title>

                      <div className="mt-5">
                        <Link to={`/dashboard/service/reports/${report?.sequelReport?.id}`}>
                          <li
                            key={report?.sequelReport?.id}
                            className="cursor-pointer relative flex space-x-6 py-6 border-2 border-blue-400 xl:static hover:bg-blue-200 rounded-xl p-2"
                          >
                            <div className="flex-auto">
                              <div className="flex items-start">
                                <h3 className="pr-10 font-semibold text-gray-900 xl:pr-0">
                                  {"Rapport "}
                                  {report?.sequelReport?.report_number}
                                </h3>
                              </div>
                            </div>
                          </li>
                        </Link>
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition.Root>
      </>
    );
  }

  return (
    <>
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="w-full sm:max-w-2xl max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="mt-3 sm:mt-5">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-gray-900"
                    >
                      {t("Create a follow-up report")}
                    </Dialog.Title>

                    <Formik
                      innerRef={formikRef}
                      initialValues={initialValues}
                      enableReinitialize={false}
                      onSubmit={async (values, helpers) => {
                        try {
                          const updateData = {
                            ...values,
                            sequel: true,
                            report_type: values.report_type || report?.report_type?.id,
                            description: values.description_text || "",
                            internal_description: values.internal_description_text || "",
                            copy_variables: values.copy_variables,
                            copy_price_lines: values.copy_price_lines,
                            checklist_config: selectedChecklistConfig,
                            items_config: selectedItemsConfig,
                          };

                          const result = await dispatch(updateReportAsync(updateData));
                          
                          if (result.payload?.id) {
                            const sequelReportId = result.payload.id;
                            
                            // Create checklist if configured
                            if (selectedChecklistConfig) {
                              try {
                                await createChecklistForSequel(sequelReportId, selectedChecklistConfig);
                              } catch (err) {
                                console.error("Error creating checklist:", err);
                                toast.error("Rapport opprettet, men sjekkliste kunne ikke opprettes");
                              }
                            }

                            props.onClose();
                            toast.success(t("Oppfølger ble opprettet"));
                            navigate(`/dashboard/service/reports/${sequelReportId}`, { replace: true });
                          } else {
                            throw new Error("Failed to create sequel report");
                          }

                          helpers.setStatus({ success: true });
                          helpers.setSubmitting(false);
                        } catch (err) {
                          toast.error("Something went wrong...");
                          console.error(err);
                          helpers.setStatus({ success: false });
                          helpers.setErrors({ submit: err.message });
                          helpers.setSubmitting(false);
                        }
                      }}
                    >
                      {(formik) => (
                        <Form>
                          <div className="grid grid-cols-6 gap-6 mt-10">
                            <div className="col-span-6 sm:col-span-3">
                              <ReportTypeSelector
                                label="Rapport Type"
                                selected={report?.report_type}
                                {...formik}
                              />
                            </div>

                            {/* Description Section */}
                            <div className="col-span-6 sm:col-span-6">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Beskrivelse
                      </label>
                      <div className="flex gap-2">
                        {report?.description && (
                          <button
                            type="button"
                            onClick={() => {
                              formik.setFieldValue("description_text", report.description);
                            }}
                            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                          >
                            Lim inn beskrivelse
                          </button>
                        )}
                        {report?.report && (
                          <button
                            type="button"
                            onClick={() => {
                              formik.setFieldValue("description_text", report.report);
                            }}
                            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                          >
                            Lim inn rapport
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="mt-2 min-h-[120px]">
                      <Suspense fallback={<div className="text-sm text-gray-500 py-4">{t("Loading")}…</div>}>
                        <QuillEditor
                          name="description_text"
                          rows={4}
                          {...formik}
                        />
                      </Suspense>
                    </div>
                    {(report?.description || report?.report) && (
                      <div className="mt-2 text-xs text-gray-500 italic">
                        Klikk &quot;Lim inn beskrivelse&quot; eller &quot;Lim inn rapport&quot; for å kopiere fra hovedrapporten
                      </div>
                    )}
                  </div>
                </div>

                {/* Internal Description Section */}
                <div className="col-span-6 sm:col-span-6">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Intern beskrivelse
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          const internalDescriptionToCopy = report?.internal_description || "";
                          formik.setFieldValue("internal_description_text", internalDescriptionToCopy);
                        }}
                        disabled={!report?.internal_description}
                        className={`text-sm font-medium ${
                          report?.internal_description
                            ? "text-blue-600 hover:text-blue-800"
                            : "text-gray-400 cursor-not-allowed"
                        }`}
                      >
                        Lim inn fra hovedrapport
                      </button>
                    </div>
                    <div className="mt-2 min-h-[120px]">
                      <Suspense fallback={<div className="text-sm text-gray-500 py-4">{t("Loading")}…</div>}>
                        <QuillEditor
                          name="internal_description_text"
                          rows={4}
                          {...formik}
                        />
                      </Suspense>
                    </div>
                    {report?.internal_description && (
                      <div className="mt-2 text-xs text-gray-500 italic">
                        Klikk &quot;Lim inn fra hovedrapport&quot; for å kopiere den interne beskrivelsen
                      </div>
                    )}
                  </div>
                </div>

                {/* Items Selection */}
                <div className="col-span-6 sm:col-span-6">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Ordrelinjer
                      </label>
                      <button
                        type="button"
                        onClick={() => setItemsSelectorOpen(true)}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        {selectedItemsConfig
                          ? "Endre valg"
                          : "Velg ordrelinjer"}
                      </button>
                    </div>
                    {selectedItemsConfig ? (
                      <div className="mt-2 text-sm text-gray-600">
                        {selectedItemsConfig.mode === "new" && (
                          <span>Ingen ordrelinjer</span>
                        )}
                        {selectedItemsConfig.mode === "main_report" && (
                          <span>
                            Fra hovedrapport (
                            {selectedItemsConfig.include_all
                              ? "alle ordrelinjer"
                              : "valgte ordrelinjer"}{" "}
                            - {selectedItemsConfig.items?.length || 0} linjer)
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="mt-2 text-sm text-gray-500 italic">
                        Ingen ordrelinjer valgt
                      </div>
                    )}
                  </div>
                </div>

                {/* Variables Selection */}
                <div className="col-span-6 sm:col-span-6">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Variabler
                      </label>
                      <PMSwitch
                        name="copy_variables"
                        label={t("report.sequel.copy_variables_switch")}
                        {...formik}
                      />
                    </div>
                    {formik.values.copy_variables ? (
                      <div className="mt-2 text-sm text-gray-600">
                        <p>
                          {reportHasVariableDataForCopy(report)
                            ? t("report.sequel.copy_variables_hint_on")
                            : t("report.sequel.copy_variables_none_available")}
                        </p>
                      </div>
                    ) : (
                      <div className="mt-2 text-sm text-gray-500 italic">
                        {t("report.sequel.copy_variables_hint_off")}
                      </div>
                    )}
                  </div>
                </div>

                {/* Price lines (same project — copy structure like variables) */}
                <div className="col-span-6 sm:col-span-6">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        {t("report.price_lines.title")}
                      </label>
                      <PMSwitch
                        name="copy_price_lines"
                        label={t("report.sequel.copy_price_lines_switch")}
                        {...formik}
                      />
                    </div>
                    {formik.values.copy_price_lines ? (
                      <div className="mt-2 text-sm text-gray-600">
                        <p>
                          {report?.price_lines && report.price_lines.length > 0
                            ? t("report.sequel.copy_price_lines_hint_on", {
                                count: report.price_lines.length,
                              })
                            : t("report.sequel.copy_price_lines_none_available")}
                        </p>
                      </div>
                    ) : (
                      <div className="mt-2 text-sm text-gray-500 italic">
                        {t("report.sequel.copy_price_lines_hint_off")}
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-span-6 sm:col-span-6">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Sjekkliste
                      </label>
                      <button
                        type="button"
                        onClick={() => setChecklistSelectorOpen(true)}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        {selectedChecklistConfig
                          ? "Endre valg"
                          : "Velg sjekkliste"}
                      </button>
                    </div>
                    {selectedChecklistConfig ? (
                      <div className="mt-2 text-sm text-gray-600">
                        {selectedChecklistConfig.mode === "new" && (
                          <span>Ny tom sjekkliste</span>
                        )}
                        {selectedChecklistConfig.mode === "type" && (
                          <span>
                            Fra type: {selectedChecklistConfig.checklist_type_title || `Type ID: ${selectedChecklistConfig.checklist_type_id}`} (
                            {selectedChecklistConfig.selected_item_ids?.length || 0}{" "}
                            punkter valgt)
                          </span>
                        )}
                        {selectedChecklistConfig.mode === "main_report" && (
                          <span>
                            Fra hovedrapport (
                            {selectedChecklistConfig.include_all
                              ? "alle punkter"
                              : "kun ikke-godkjente"}{" "}
                            - {selectedChecklistConfig.items?.length || 0} punkter)
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="mt-2 text-sm text-gray-500 italic">
                        Ingen sjekkliste valgt
                      </div>
                    )}
                  </div>
                </div>
                          </div>

                          <div className="mt-20 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                            <button
                              type="button"
                              className="inline-flex justify-center w-20 rounded-md border border-transparent bg-gray-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
                              onClick={onClose}
                            >
                              {t("Close")}
                            </button>

                            <button
                              type="button"
                              className="inline-flex align-right justify-center rounded-md border border-transparent bg-emerald-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
                              onClick={formik.submitForm}
                            >
                              {t("Create Sequel")}
                            </button>
                          </div>

                          <SimplePrompt
                            title="Slett rapport"
                            description="Er du sikker på at du ønsker å slette rapporten?"
                            onAccept={() => formik.setFieldValue("delete", true)}
                            open={deletePrompt}
                            onClose={() => setDeletePrompt(false)}
                          />

                          <EnhancedChecklistSelector
                            open={checklistSelectorOpen}
                            onClose={() => setChecklistSelectorOpen(false)}
                            onSelect={handleChecklistSelect}
                            mainReportChecklist={report?.checklist}
                            mainReport={report}
                          />

                          <EnhancedItemsSelector
                            open={itemsSelectorOpen}
                            onClose={() => setItemsSelectorOpen(false)}
                            onSelect={handleItemsSelect}
                            mainReportItems={report?.items}
                            mainReport={report}
                          />
                        </Form>
                      )}
                    </Formik>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};
